import { useState } from 'react';
import { useSaveSignalSettings, useSignalSettings } from '@/hooks/leverageQueries';
import { useToast } from '@/hooks/useToast';
import { Field, Modal } from '@/components/ui';
import { IconCheck } from '@/components/Icons';
import type { SignalSettings, SignalSettingsInput } from '@/lib/types';

/** "02/10 às 22:12", no relógio de quem olha. */
const formatInstant = (iso: string) => {
  const date = new Date(iso);
  return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

function Status({ settings }: { settings: SignalSettings }) {
  if (!settings.hasToken) {
    return <div className="lev-signal-status">Ainda sem token. Cole abaixo o token do painel de admin da corretora.</div>;
  }
  return (
    <div className="lev-signal-status" data-state={settings.expired ? 'expired' : settings.ready ? 'ok' : undefined}>
      <span>
        Conta do painel: <strong>{settings.owner}</strong>
      </span>
      <span>
        {settings.expiresAt
          ? settings.expired
            ? `O token venceu ${formatInstant(settings.expiresAt)}. Entre no painel da corretora e cole um novo.`
            : `O token vale até ${formatInstant(settings.expiresAt)}.`
          : 'O token não diz quando vence.'}
      </span>
      {!settings.hasApiKey ? <span>Falta a api-key.</span> : null}
      {!settings.slot ? <span>Falta o slot.</span> : null}
    </div>
  );
}

/**
 * A integração com a api de sinais da corretora. O token é a sessão do painel
 * de admin dela (vence em horas): quando vencer, é só colar o novo aqui.
 * Token e api-key nunca voltam para a tela — vazio mantém o que está guardado.
 */
export function SignalSettingsModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const { data: settings, isLoading } = useSignalSettings();
  const save = useSaveSignalSettings();
  const [token, setToken] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [slot, setSlot] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);

  const slotValue = slot ?? settings?.slot ?? '';
  const descriptionValue = description ?? settings?.description ?? '';

  async function submit() {
    const body: SignalSettingsInput = {
      ...(token.trim() ? { token: token.trim() } : {}),
      ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      ...(slotValue.trim() ? { slot: slotValue.trim() } : {}),
      description: descriptionValue.trim(),
    };
    try {
      await save.mutateAsync(body);
      toast.success('Configuração dos sinais salva.');
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal
      title="Sinais da corretora"
      width={620}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={save.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={save.isPending || isLoading || !settings?.storageEnabled}>
            {save.isPending ? <span className="spinner" /> : <IconCheck />} Salvar
          </button>
        </>
      }
    >
      {isLoading || !settings ? (
        <div className="skeleton" style={{ height: 180 }} />
      ) : (
        <>
          {!settings.storageEnabled ? (
            <div className="alert alert-error" role="alert">O servidor está sem CREDENTIALS_KEY: sem ela, não dá para guardar o token.</div>
          ) : null}
          <Status settings={settings} />

          <Field
            label={settings.hasToken ? 'Token novo' : 'Token *'}
            hint="No painel de admin da corretora, o cabeçalho Authorization de qualquer requisição. Pode colar com o “Bearer”."
          >
            <textarea
              className="inp lev-token-input"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder={settings.hasToken ? 'Vazio mantém o token guardado' : 'eyJhbGciOi…'}
              spellCheck={false}
              autoComplete="off"
            />
          </Field>
          <Field label={settings.hasApiKey ? 'Api-key nova' : 'Api-key *'} hint="O cabeçalho api-key da mesma requisição.">
            <input
              className="inp"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder={settings.hasApiKey ? 'Vazio mantém a guardada' : ''}
              spellCheck={false}
              autoComplete="off"
            />
          </Field>
          <div className="lev-settings-row">
            <Field label="Slot *" hint="Onde o sinal aparece na corretora.">
              <input className="inp" value={slotValue} onChange={(event) => setSlot(event.target.value)} placeholder="mybroker-9" maxLength={60} />
            </Field>
            <Field label="Descrição" hint="Vai junto com o sinal.">
              <input className="inp" value={descriptionValue} onChange={(event) => setDescription(event.target.value)} placeholder="No data" maxLength={200} />
            </Field>
          </div>
        </>
      )}
    </Modal>
  );
}
